import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PackingApiAdapter } from '../adapters/PackingApiAdapter';
import { createHttpClient } from '@shared/adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useToast } from '@shared/hooks/use-toast';

export const usePrintOrder = () => {
  const { t } = useTranslation('packingModule');
  const { toast } = useToast();
  const [loadingOrders, setLoadingOrders] = useState<Set<number>>(new Set());

  const printOrder = async (orderId: number, printerCode?: string) => {
    try {
      // Agregar el orderId al Set de pedidos en loading
      setLoadingOrders((prev) => new Set(prev).add(orderId));
      const api = new PackingApiAdapter(createHttpClient(apiClientWH));

      // Siempre usar PDF como método de impresión
      const printingMethod = 'pdf';

      console.log('🔍 usePrintOrder - Printing order:', {
        orderId,
        printingMethod,
        printerCode,
      });

      // Llamar a la API para imprimir
      const response = await api.printOrder(
        orderId,
        printingMethod,
        printerCode
      );

      console.log('🔍 usePrintOrder - Response:', response);

      // Verificar que la respuesta tenga el link
      // La respuesta puede venir como string directo, array o como objeto
      let linkUrl = null;

      if (typeof response === 'string') {
        // Si la respuesta ES directamente el string del link
        linkUrl = response;
      } else if (Array.isArray(response) && response.length > 0) {
        // Si es un array, tomar el primer elemento
        linkUrl = response[0];
      } else if (response && response.link) {
        // Si es un objeto con propiedad link
        linkUrl = response.link;
      }

      if (linkUrl) {
        // Mostrar toast de éxito ANTES de abrir la pestaña
        toast({
          title: t('toasts.print_success.title', 'Etiqueta generada'),
          description: t(
            'toasts.print_success.description',
            'La etiqueta fue generada y será redirigida en unos segundos'
          ),
          variant: 'default',
        });

        // Pequeño delay para asegurar que el toast se muestre
        setTimeout(() => {
          // Asegurar que linkUrl sea un string válido
          const finalUrl = String(linkUrl).trim();

          if (finalUrl && finalUrl.startsWith('http')) {
            window.open(finalUrl, '_blank');
          } else {
            toast({
              title: t('toasts.print_error.title', 'Error'),
              description: 'URL de la etiqueta no es válida',
              variant: 'destructive',
            });
          }
        }, 100);
      } else {
        toast({
          title: t('toasts.print_error.title', 'Error'),
          description: 'No se recibió el link de la etiqueta',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.log('🔍 usePrintOrder - Error:', error);

      // Mostrar error de la API
      const errorMessage =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        t('toasts.print_error.description', 'Error al generar la etiqueta');

      toast({
        title: t('toasts.print_error.title', 'Error'),
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      // Remover el orderId del Set de pedidos en loading
      setLoadingOrders((prev) => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  // Función para verificar si un pedido específico está en loading
  const isOrderLoading = (orderId: number) => loadingOrders.has(orderId);

  return { printOrder, isOrderLoading };
};
